import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DollarSign, ShoppingBag, Users, Clock, AlertTriangle, Truck } from "lucide-react";
import { DateRangePicker } from "@/components/admin/DateRangePicker";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

export default function CompanyReports() {
  const [range, setRange] = useState("today");
  const [custom, setCustom] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const qs = range === "custom" && custom
      ? `range=custom&start=${custom.start}&end=${custom.end}`
      : `range=${range}`;
    api.get(`/reports/company?${qs}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range, custom]);

  const applyCustom = ({ start, end }) => { setCustom({ start, end }); setRange("custom"); };

  if (!data) return <div className="p-8 text-neutral-500">Loading company reports…</div>;
  const t = data.totals;
  const cards = [
    {
      icon: DollarSign,
      label: "Total Online Sales",
      value: `$${t.sales.toFixed(2)}`,
      testid: "co-sales",
    },
    { icon: ShoppingBag, label: "Online Orders", value: t.orders, testid: "co-orders" },
    { icon: Users, label: "Clocked In", value: t.clocked_in, testid: "co-clocked" },
    { icon: Clock, label: "Labor Hours", value: t.labor_hours.toFixed(1), testid: "co-labor" },
    { icon: AlertTriangle, label: "Low-Stock Items", value: t.low_stock, testid: "co-low", warn: t.low_stock > 0 },
    { icon: Truck, label: "Restock Pending", value: t.restock_pending, testid: "co-restock", warn: t.restock_pending > 0 },
  ];

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Company · Website Orders</p>
          <h1
            className="font-display font-bold text-3xl tracking-tight"
            data-testid="company-reports-title"
          >
            Online Sales Reports
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 h-8 rounded-full text-xs font-medium ${range === r.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"}`}
                data-testid={`co-range-${r.key}`}
              >{r.label}</button>
            ))}
          </div>
          <DateRangePicker
            value={range === "custom" && custom ? { from: new Date(custom.start + "T00:00:00"), to: new Date(custom.end + "T00:00:00") } : null}
            onApply={applyCustom}
          />
        </div>
      </div>

      <p
        className="mb-5 text-sm leading-relaxed text-neutral-500"
        data-testid="company-online-sales-scope-note"
      >
        Company reports summarize website orders only. Square remains the source of truth for
        in-person Fort Meade sales.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {cards.map(({ icon: Icon, label, value, testid, warn }) => (
          <div key={testid} className="bg-white rounded-2xl border border-neutral-200/70 p-5" data-testid={testid}>
            <div className={`w-9 h-9 rounded-xl inline-flex items-center justify-center mb-3 ${warn ? "bg-orange-50 text-orange-600" : "bg-neutral-100 text-neutral-700"}`}>
              <Icon className="w-4 h-4"/>
            </div>
            <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
            <div className="mt-1 font-display font-bold text-2xl">{value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <div className="p-5 border-b border-neutral-100">
          <h2 className="font-display font-semibold">Online store performance</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Store</th>
              <th className="p-4">Online sales</th>
              <th className="p-4">Online orders</th>
              <th className="p-4">On shift</th>
              <th className="p-4">Labor hrs</th>
              <th className="p-4">Low stock</th>
              <th className="p-4">Restock pending</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {data.stores.map((s) => (
              <tr key={s.id} data-testid={`co-store-${s.id}`}>
                <td className="p-4">
                  <Link to={`/admin/stores/${s.id}`} className="font-medium hover:text-[hsl(var(--primary))]">{s.name}</Link>
                </td>
                <td className="p-4 font-semibold">${s.sales.toFixed(2)}</td>
                <td className="p-4">{s.orders}</td>
                <td className="p-4">{s.clocked_in}</td>
                <td className="p-4">{s.labor_hours.toFixed(1)}</td>
                <td className="p-4"><span className={s.low_stock > 0 ? "text-orange-600 font-semibold" : "text-neutral-500"}>{s.low_stock}</span></td>
                <td className="p-4"><span className={s.restock_pending > 0 ? "text-orange-600 font-semibold" : "text-neutral-500"}>{s.restock_pending}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
